import type { ColumnDef } from "@tanstack/react-table";

import { RelativeTime } from "@/components/Dispatch/FieldValue";
import { useMinuteClock } from "@/hooks/useMinuteClock";
import type { DriverRow } from "@/types/driver";
import ColumnHeader from "@/ui/ColumnHeader";
import type { DataTableFeatures } from "@/ui/dataTableFeatures";
import { MESSAGE_TIME_COLUMN_ID } from "./columnIds";
import { columnWidthFloors } from "./columnMinWidth";

const MESSAGE_TIME_COLUMN_TITLE = "Message time";

/** Wide enough on first load for the longest value, `Today, 12:32 PM (42 min ago)`, before the user resizes. */
const MESSAGE_TIME_INITIAL_MIN_WIDTH_PX = 210;

/**
 * When the row's most recent driver message was sent, as the Dispatch view's Time column shows it: the local clock
 * with the age beside it, the full date on hover. The clock is 12-hour with AM and PM, as the feed captions a
 * message, and a message from today is labeled `Today`, so every value names its day. Renders nothing when the driver has sent no message.
 */
export function MessageTimeCell({ iso }: { iso: string | null }) {
  const now = useMinuteClock();
  if (!iso) return null;
  return <RelativeTime iso={iso} now={now} labelToday clockStyle="12h" />;
}

export function messageTimeColumn(): ColumnDef<DataTableFeatures, DriverRow> {
  const { minSize, initialMinWidth } = columnWidthFloors(MESSAGE_TIME_COLUMN_TITLE, {
    draggable: true,
    hasFilter: false,
  });
  return {
    accessorKey: MESSAGE_TIME_COLUMN_ID,
    header: () => <ColumnHeader title={MESSAGE_TIME_COLUMN_TITLE} />,
    cell: ({ getValue }) => <MessageTimeCell iso={getValue<string | null | undefined>() ?? null} />,
    meta: { flex: 1, draggable: true, initialMinWidth: Math.max(initialMinWidth, MESSAGE_TIME_INITIAL_MIN_WIDTH_PX) },
    minSize,
    enableSorting: false,
    enableGlobalFilter: false,
  };
}
